/**
 * <s-art-story> — one-slide-at-a-time artwork carousel on a native scroll-snap track (swipe works natively).
 *
 * Marks the visible slide with data-active (others get data-inactive), updates the 01 / 05 counter and progress
 * line, builds thumbnail buttons from each slide's data-thumb / data-title, and optionally autoplays slowly.
 * Autoplay never runs for reduced-motion visitors, only while the carousel is on screen, and stops for good
 * as soon as the visitor interacts.
 *
 * Commands: --prev, --next (from buttons with commandfor pointing at this element).
 * Children: [data-art-track] with [data-art-slide] items; optional [data-art-current], [data-art-total],
 * [data-art-progress], [data-art-thumbs] + template[data-art-thumb-template] (one button with an img).
 * Data: data-autoplay="true|false", data-interval (seconds), data-label-goto ("Show artwork %n: %t").
 */
class SArtStory extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #index = 0;
  #timer = null;
  #visible = false;
  #stopped = false;
  #motion;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#track = this.querySelector('[data-art-track]');
    if (!this.#track) return;
    this.#slides = [...this.#track.querySelectorAll('[data-art-slide]')];
    if (!this.#slides.length) return;
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.#buildThumbs();
    const total = this.querySelector('[data-art-total]');
    if (total) total.textContent = String(this.#slides.length).padStart(2, '0');

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.#slides.indexOf(entry.target));
        }
      },
      { root: this.#track, threshold: 0.6 },
    );
    this.#slides.forEach((slide) => io.observe(slide));
    signal.addEventListener('abort', () => io.disconnect());

    const viewIo = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#syncAutoplay();
    });
    viewIo.observe(this);
    signal.addEventListener('abort', () => viewIo.disconnect());

    this.addEventListener(
      'command',
      (event) => {
        if (event.command === '--next') this.#go(this.#index + 1);
        if (event.command === '--prev') this.#go(this.#index - 1);
      },
      { signal },
    );

    const stop = () => this.#stopAutoplay();
    for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'focusin']) {
      this.addEventListener(type, stop, { signal, passive: true });
    }
    this.#motion.addEventListener('change', () => this.#syncAutoplay(), { signal });
    document.addEventListener('visibilitychange', () => this.#syncAutoplay(), { signal });

    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
  }

  #buildThumbs() {
    const holder = this.querySelector('[data-art-thumbs]');
    const template = this.querySelector('template[data-art-thumb-template]');
    if (!holder || !template) return;
    const label = this.dataset.labelGoto || '%n: %t';
    holder.replaceChildren();
    this.#thumbs = this.#slides.map((slide, i) => {
      const button = template.content.firstElementChild.cloneNode(true);
      button.setAttribute('aria-label', label.replace('%n', String(i + 1)).replace('%t', slide.dataset.title || ''));
      const img = button.querySelector('img');
      if (img && slide.dataset.thumb) img.src = slide.dataset.thumb;
      else img?.remove();
      button.addEventListener('click', () => this.#go(i), { signal: this.#ac.signal });
      holder.append(button);
      return button;
    });
  }

  #go(i) {
    const n = this.#slides.length;
    const next = ((i % n) + n) % n;
    const behavior = this.#motion.matches ? 'auto' : 'smooth';
    const slide = this.#slides[next];
    const trackBox = this.#track.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    this.#track.scrollBy({ left: slideBox.left - trackBox.left, behavior });
    this.#setActive(next);
  }

  #setActive(i) {
    if (i < 0) return;
    this.#index = i;
    this.#slides.forEach((slide, n) => {
      slide.toggleAttribute('data-active', n === i);
      slide.toggleAttribute('data-inactive', n !== i);
      slide.setAttribute('aria-hidden', n === i ? 'false' : 'true');
      slide.inert = n !== i;
    });
    this.#thumbs.forEach((thumb, n) => thumb.setAttribute('aria-current', n === i ? 'true' : 'false'));
    const current = this.querySelector('[data-art-current]');
    if (current) current.textContent = String(i + 1).padStart(2, '0');
    const progress = this.querySelector('[data-art-progress]');
    if (progress) progress.style.width = `${((i + 1) / this.#slides.length) * 100}%`;
  }

  #syncAutoplay() {
    const allowed =
      this.dataset.autoplay === 'true' &&
      !this.#stopped &&
      this.#visible &&
      !document.hidden &&
      !this.#motion.matches &&
      this.#slides.length > 1;
    if (allowed && !this.#timer) {
      const seconds = Math.max(5, parseFloat(this.dataset.interval) || 8);
      this.#timer = setInterval(() => this.#go(this.#index + 1), seconds * 1000);
    } else if (!allowed && this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #stopAutoplay() {
    this.#stopped = true;
    this.#syncAutoplay();
  }
}

if (!customElements.get('s-art-story')) {
  customElements.define('s-art-story', SArtStory);
}
